import React, { useEffect, useRef } from 'react';
import {
  COUNT_COMPLETION_NEXT_ACTION,
  COUNT_COMPLETION_STEP,
} from '../utils/countCompletionWorkflow';

const ACTION = '#C0603F';
const INK = '#3E2723';
const SOFT_INK = '#74645C';
const LINE = '#E8DDD0';
const SURFACE = '#FFFDF9';
const GOOD = '#4CAF50';
const WARNING = '#F9A825';
const ERROR = '#B23A36';

const itemLabel = (count) => `${count} ${count === 1 ? 'item' : 'items'}`;

// Money renders exactly as the Inventory Value report renders it, so the
// completed screen and the report it links to read as the same numbers.
const currencyFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const formatMoney = (value) => currencyFormatter.format(Number.isFinite(value) ? value : 0);

const CountedPieIcon = ({ counted, total }) => {
  const pct = total > 0 ? Math.min(1, Math.max(0, counted / total)) : 1;
  const theta = pct * Math.PI * 2;
  const largeArc = theta > Math.PI ? 1 : 0;
  const x = (12 + 10 * Math.sin(theta)).toFixed(2);
  const y = (12 - 10 * Math.cos(theta)).toFixed(2);
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="10" fill={WARNING} />
      {pct >= 1 ? (
        <circle cx="12" cy="12" r="10" fill={GOOD} />
      ) : pct > 0 ? (
        <path d={`M12 12 L12 2 A10 10 0 ${largeArc} 1 ${x} ${y} Z`} fill={GOOD} />
      ) : null}
    </svg>
  );
};

const BlankCursorIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke={WARNING} strokeWidth="2.6" strokeLinecap="round">
    <path d="M12 4.5v15" />
    <path d="M8.5 4.5h7" />
    <path d="M8.5 19.5h7" />
  </svg>
);

const TruckIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke={SOFT_INK} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2.5 6.5h10v8h-10z" />
    <path d="M12.5 10h3.6l3.2 3.2v1.3h-6.8z" />
    <circle cx="6.8" cy="16.3" r="1.8" fill={SURFACE} />
    <circle cx="16.2" cy="16.3" r="1.8" fill={SURFACE} />
  </svg>
);

const SavedIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="10" fill={GOOD} />
    <path d="M7.5 12.4l3 3 6-6.4" fill="none" stroke="#FFF8F4" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const StatusRow = ({ icon, children, detail, testId }) => (
  <div className="ct-completion-status-row" data-testid={testId}>
    <span className="ct-completion-status-icon" aria-hidden="true">{icon}</span>
    <div>
      <div className="ct-completion-status-label">{children}</div>
      {detail ? <div className="ct-completion-status-detail">{detail}</div> : null}
    </div>
  </div>
);

const ErrorNotice = ({ children }) => children ? (
  <div className="ct-completion-error" role="alert">
    <strong>Something needs attention</strong>
    <span>{children}</span>
  </div>
) : null;

const PrimaryButton = ({ children, onClick, disabled, testId }) => (
  <button
    type="button"
    className="ct-completion-button ct-completion-primary"
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
  >
    {children}
  </button>
);

const QuietButton = ({ children, onClick, disabled, testId }) => (
  <button
    type="button"
    className="ct-completion-button ct-completion-quiet"
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
  >
    {children}
  </button>
);

const DangerButton = ({ children, onClick, disabled, testId, solid = false }) => (
  <button
    type="button"
    className={`ct-completion-button ct-completion-danger${solid ? ' ct-completion-danger-solid' : ''}`}
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
  >
    {children}
  </button>
);

export default function CountWorkflowModal({
  step,
  summary = {},
  locationChoice = {},
  orderingEnabled = false,
  isEopSession = false,
  nextAction = COUNT_COMPLETION_NEXT_ACTION.NONE,
  savingsReviewId = null,
  error = null,
  isBusy = false,
  onBack,
  onDoneWithLocation,
  onDoneWithCount,
  onFillUncounted,
  onAbandon,
  onConfirmAbandon,
  onComplete,
  onDone,
  onOpenReport,
  onViewSavingsReview,
  onCreateDraftOrders,
}) {
  const actionLockRef = useRef(false);
  useEffect(() => {
    if (!isBusy) actionLockRef.current = false;
  }, [isBusy, error, step]);

  const isLocationChoice = step === COUNT_COMPLETION_STEP.LOCATION_CHOICE;
  const isReview = step === COUNT_COMPLETION_STEP.REVIEW;
  const isAbandonConfirm = step === COUNT_COMPLETION_STEP.ABANDON_CONFIRM;
  const isCompleted = step === COUNT_COMPLETION_STEP.COMPLETED;
  if (!isLocationChoice && !isReview && !isAbandonConfirm && !isCompleted) return null;

  const countedItems = Number(summary.countedItems) || 0;
  const totalItems = Number(summary.totalItems) || 0;
  const unaccountedItems = Number(summary.unaccountedItems) || 0;
  const orderingNeedCount = Number(summary.orderingNeedCount) || 0;
  // EOP completion summary: order runs keep their ordering ending — the EOP
  // ending is for period-end/full-inventory counts only.
  const eopCompleted = isCompleted && isEopSession && !orderingEnabled;
  // undefined = still loading from the frozen rows; null = load failed (the
  // section is omitted — the report links still answer the same question).
  const valueByCategory = summary.valueByCategory;
  const groupNoun = locationChoice.groupNoun || 'location';
  const nextGroupLabel = locationChoice.nextGroupLabel || null;
  const runOnce = (action) => {
    if (actionLockRef.current || isBusy) return;
    actionLockRef.current = true;
    action?.();
  };

  const renderOrderingRows = () => {
    if (!orderingEnabled) return null;
    return (
      <StatusRow
        icon={<TruckIcon />}
        testId="completion-ordering-need"
        detail={orderingNeedCount > 0 ? 'Continue to review and place orders.' : null}
      >
        {orderingNeedCount > 0
          ? `${itemLabel(orderingNeedCount)} generated an order`
          : 'Nothing needs ordering'}
      </StatusRow>
    );
  };

  // Category valuation card on the EOP ending. The math comes from the same
  // helpers the Inventory Value report runs (summarizeCountValueByCategory in
  // utils/countItemReporting, computed over the frozen close snapshots), so
  // these totals always equal the report the pills below open.
  const renderEopValueSummary = () => {
    if (!eopCompleted || valueByCategory === null) return null;
    if (valueByCategory === undefined) {
      return (
        <div className="ct-completion-value" data-testid="completion-eop-value-loading">
          <h3>Inventory value by category</h3>
          <p className="ct-completion-value-loading">Calculating totals…</p>
        </div>
      );
    }
    const categories = Array.isArray(valueByCategory.categories) ? valueByCategory.categories : [];
    return (
      <div className="ct-completion-value" data-testid="completion-eop-value">
        <h3>Inventory value by category</h3>
        <div className="ct-completion-value-list">
          {categories.map((entry) => (
            <div className="ct-completion-value-row" key={entry.category}>
              <span className="ct-completion-value-name">{entry.category}</span>
              <span className="ct-completion-value-amount">{formatMoney(entry.value)}</span>
            </div>
          ))}
          <div className="ct-completion-value-row ct-completion-value-total" data-testid="completion-eop-total">
            <span className="ct-completion-value-name">Total inventory value</span>
            <span className="ct-completion-value-amount">{formatMoney(valueByCategory.totalValue)}</span>
          </div>
        </div>
      </div>
    );
  };

  // Report pills on the EOP ending — the same report ids the completed count
  // card's "Available Reports" pills open (Reports.jsx REPORT_TYPES), shown
  // here so the reports are one tap away before Done dismisses the screen.
  const renderEopReportLinks = () => {
    if (!eopCompleted || !onOpenReport) return null;
    return (
      <div className="ct-completion-reports" data-testid="completion-eop-reports">
        <h3>Available reports</h3>
        <div className="ct-completion-report-pills">
          <button
            type="button"
            className="ct-completion-report-pill"
            onClick={() => onOpenReport('detail')}
            data-testid="completion-open-detail-report"
            title="Open the Inventory Detail report for this count without leaving this page"
          >
            <span aria-hidden="true">📋</span> Inventory Detail
          </button>
          <button
            type="button"
            className="ct-completion-report-pill"
            onClick={() => onOpenReport('value')}
            data-testid="completion-open-value-report"
            title="Open the Inventory Value report for this count without leaving this page"
          >
            <span aria-hidden="true">💰</span> Inventory Value
          </button>
        </div>
      </div>
    );
  };

  const renderCompletedNextStep = () => {
    if (orderingEnabled && nextAction === COUNT_COMPLETION_NEXT_ACTION.PREPARING_ORDERING) {
      return (
        <div className="ct-completion-next-step" aria-live="polite">
          <h3>Preparing the ordering next step</h3>
          <p>Checking vendor options. No purchase order is being created or sent.</p>
          <PrimaryButton disabled={true} testId="count-complete-preparing-ordering">
            Preparing ordering options…
          </PrimaryButton>
        </div>
      );
    }

    if (!orderingEnabled || nextAction === COUNT_COMPLETION_NEXT_ACTION.NONE) {
      return (
        <div className="ct-completion-next-step">
          {orderingEnabled ? (
            <p>No orders are needed from this count.</p>
          ) : null}
          <PrimaryButton onClick={onDone} disabled={isBusy} testId="count-complete-done">
            Done
          </PrimaryButton>
        </div>
      );
    }

    if (nextAction === COUNT_COMPLETION_NEXT_ACTION.SAVINGS_REVIEW) {
      return (
        <div className="ct-completion-next-step">
          <h3>Compare vendors before creating orders</h3>
          <p>
            Your purchase plan is ready. No purchase order is created until you finish a vendor group.
          </p>
          <PrimaryButton
            onClick={() => onViewSavingsReview?.(savingsReviewId)}
            disabled={isBusy || !savingsReviewId}
            testId="count-complete-view-savings-review"
          >
            Open Purchase Planner
          </PrimaryButton>
          <QuietButton onClick={onDone} disabled={isBusy} testId="count-complete-done-for-now">
            Done for now
          </QuietButton>
        </div>
      );
    }

    if (nextAction === COUNT_COMPLETION_NEXT_ACTION.CREATE_DRAFTS) {
      return (
        <div className="ct-completion-next-step">
          <h3>Ready to create draft orders</h3>
          <p>Create drafts now, then review them on the Ordering page. Nothing will be sent.</p>
          <PrimaryButton
            onClick={() => runOnce(onCreateDraftOrders)}
            disabled={isBusy}
            testId="count-complete-create-drafts"
          >
            {isBusy ? 'Creating draft orders…' : 'Continue to create orders'}
          </PrimaryButton>
          <QuietButton onClick={onDone} disabled={isBusy} testId="count-complete-done-for-now">
            Done for now
          </QuietButton>
        </div>
      );
    }

    return (
      <div className="ct-completion-next-step">
        <p>Your count is safe. Open Ordering later to continue preparing orders.</p>
        <PrimaryButton onClick={onDone} disabled={isBusy} testId="count-complete-done">
          Done
        </PrimaryButton>
      </div>
    );
  };

  const workflowView = isLocationChoice
    ? 'location-choice'
    : (isReview ? 'review' : (isAbandonConfirm ? 'abandon-confirm' : 'completed'));
  const eyebrow = isLocationChoice
    ? 'Finish count'
    : (isReview ? 'Complete count' : (isAbandonConfirm ? 'Abandon count' : 'Count saved'));
  const title = isLocationChoice
    ? 'Are you done?'
    : (isReview ? 'Complete this count?' : (isAbandonConfirm ? 'Abandon this count?' : 'Count complete'));

  return (
    <div className="ct-completion-overlay" role="presentation">
      <style>{`
        .ct-completion-overlay {
          position: fixed; inset: 0; z-index: 10020; display: flex;
          align-items: center; justify-content: center; padding: 24px;
          background: rgba(37, 26, 21, 0.48); font-family: Montserrat, sans-serif;
        }
        .ct-completion-dialog {
          width: min(100%, 520px); max-height: calc(100vh - 48px); overflow-y: auto;
          background: ${SURFACE}; border: 1px solid ${LINE}; border-radius: 18px;
          box-shadow: 0 24px 64px rgba(62, 39, 35, 0.22); padding: 30px;
        }
        .ct-completion-eyebrow {
          margin: 0 0 8px; color: ${SOFT_INK}; font-size: 11px; font-weight: 800;
          letter-spacing: .14em; text-transform: uppercase;
        }
        .ct-completion-title-row { display: flex; align-items: center; gap: 12px; margin-bottom: 8px; }
        .ct-completion-success {
          display: inline-flex; flex: 0 0 34px; width: 34px; height: 34px; align-items: center;
          justify-content: center; border-radius: 50%; background: ${GOOD}; color: white;
          font-size: 20px; font-weight: 800;
        }
        .ct-completion-dialog h2 { margin: 0; color: ${INK}; font-size: 25px; line-height: 1.2; font-weight: 800; }
        .ct-completion-lede { margin: 0 0 22px; color: ${SOFT_INK}; font-size: 14px; line-height: 1.55; }
        .ct-completion-choice-copy, .ct-completion-abandon-copy {
          margin: 0 0 22px; color: ${SOFT_INK}; font-size: 14px; line-height: 1.55;
        }
        .ct-completion-choice-copy strong { color: ${INK}; }
        .ct-completion-abandon-copy {
          padding: 13px 14px; border: 1px solid rgba(178,58,54,.35); border-radius: 11px;
          background: rgba(178,58,54,.06);
        }
        .ct-completion-status-list {
          display: grid; gap: 0; margin: 0 0 22px; border: 1px solid ${LINE};
          border-radius: 13px; overflow: hidden; background: white;
        }
        .ct-completion-status-row { display: flex; gap: 11px; align-items: flex-start; padding: 13px 15px; }
        .ct-completion-status-row + .ct-completion-status-row { border-top: 1px solid ${LINE}; }
        .ct-completion-status-icon {
          flex: 0 0 24px; width: 24px; height: 24px; display: inline-flex;
          align-items: center; justify-content: center;
        }
        .ct-completion-status-icon svg { width: 22px; height: 22px; display: block; }
        .ct-completion-status-label { color: ${INK}; font-size: 13px; font-weight: 700; line-height: 1.4; }
        .ct-completion-status-detail { margin-top: 3px; color: ${SOFT_INK}; font-size: 12px; line-height: 1.45; }
        .ct-completion-meaning { margin: 0 0 22px; padding: 0 2px; }
        .ct-completion-meaning h3, .ct-completion-next-step h3,
        .ct-completion-value h3, .ct-completion-reports h3 {
          margin: 0 0 7px; color: ${INK}; font-size: 12px; font-weight: 800;
          letter-spacing: .08em; text-transform: uppercase;
        }
        .ct-completion-value { margin: 0 0 20px; }
        .ct-completion-value-loading { margin: 0; color: ${SOFT_INK}; font-size: 13px; }
        .ct-completion-value-list {
          border: 1px solid ${LINE}; border-radius: 13px; background: white;
          overflow: hidden auto; max-height: 234px;
        }
        .ct-completion-value-row {
          display: flex; align-items: baseline; justify-content: space-between;
          gap: 12px; padding: 9px 15px; font-size: 13px; color: ${INK};
        }
        .ct-completion-value-row + .ct-completion-value-row { border-top: 1px solid ${LINE}; }
        .ct-completion-value-name {
          min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
        .ct-completion-value-amount { flex: none; font-weight: 700; font-variant-numeric: tabular-nums; }
        .ct-completion-value-total {
          position: sticky; bottom: 0; background: #FDFAF6; font-weight: 800;
          border-top: 1px solid ${LINE};
        }
        .ct-completion-reports { margin: 0 0 20px; }
        .ct-completion-report-pills { display: flex; flex-wrap: wrap; gap: 7px; }
        .ct-completion-report-pill {
          display: inline-flex; align-items: center; gap: 5px; padding: 7px 14px;
          border: 1px solid #C9B8A8; border-radius: 999px; background: white;
          color: #5A4A42; font: inherit; font-size: 13px; font-weight: 600;
          line-height: 1.4; cursor: pointer; white-space: nowrap;
          transition: background-color 0.12s;
        }
        .ct-completion-report-pill:hover { background: #F5EDE4; }
        .ct-completion-meaning p, .ct-completion-next-step p {
          margin: 0; color: ${SOFT_INK}; font-size: 13px; line-height: 1.55;
        }
        .ct-completion-error {
          display: grid; gap: 3px; margin: 0 0 18px; padding: 12px 14px;
          border: 1px solid rgba(178,58,54,.35); border-radius: 11px;
          background: rgba(178,58,54,.06); color: ${ERROR}; font-size: 12px; line-height: 1.45;
        }
        .ct-completion-actions, .ct-completion-next-step { display: grid; gap: 10px; }
        .ct-completion-next-step { padding-top: 3px; }
        .ct-completion-next-step p { margin-bottom: 5px; }
        .ct-completion-button {
          min-height: 48px; width: 100%; padding: 11px 18px; border-radius: 12px;
          font: inherit; font-size: 14px; font-weight: 800; cursor: pointer;
        }
        .ct-completion-primary { border: 1px solid ${ACTION}; background: ${ACTION}; color: #FFF8F4; }
        .ct-completion-quiet { border: 1px solid #CBBCAF; background: transparent; color: ${INK}; }
        .ct-completion-danger { border: 1px solid ${ERROR}; background: transparent; color: ${ERROR}; }
        .ct-completion-danger-solid { background: ${ERROR}; color: #FFF8F4; }
        .ct-completion-button:disabled { cursor: not-allowed; opacity: .6; }
        @media (max-width: 600px) {
          .ct-completion-overlay { align-items: flex-end; padding: 10px; padding-bottom: max(10px, env(safe-area-inset-bottom)); }
          .ct-completion-dialog { width: 100%; max-height: calc(100dvh - 20px); border-radius: 18px; padding: 20px 20px 16px; }
          .ct-completion-dialog h2 { font-size: 21px; }
          .ct-completion-lede { display: none; }
          .ct-completion-choice-copy, .ct-completion-abandon-copy { margin-bottom: 16px; font-size: 13px; }
          .ct-completion-status-list { margin-bottom: 16px; }
          .ct-completion-status-row { padding: 10px 12px; }
          .ct-completion-value, .ct-completion-reports { margin-bottom: 16px; }
          .ct-completion-value-list { max-height: 190px; }
          .ct-completion-value-row { padding: 8px 12px; }
          .ct-completion-actions, .ct-completion-next-step { gap: 8px; }
          .ct-completion-button { min-height: 46px; }
        }
      `}</style>
      <section
        className="ct-completion-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ct-completion-title"
        data-testid={`count-workflow-${workflowView}`}
      >
        <p className="ct-completion-eyebrow">{eyebrow}</p>
        <div className="ct-completion-title-row">
          {isCompleted ? <span className="ct-completion-success" aria-hidden="true">✓</span> : null}
          <h2 id="ct-completion-title">{title}</h2>
        </div>
        {isLocationChoice ? (
          <>
            <p className="ct-completion-choice-copy">
              Nothing has been entered in <strong>{locationChoice.untouchedNames}</strong>. Done closes out the
              whole count — or move to the next {groupNoun} instead.
            </p>
            <div className="ct-completion-actions">
              <PrimaryButton onClick={onDoneWithLocation} disabled={isBusy} testId="count-location-done">
                {nextGroupLabel
                  ? `Done with ${groupNoun} — go to ${nextGroupLabel}`
                  : `Done with ${groupNoun}`}
              </PrimaryButton>
              <QuietButton onClick={onDoneWithCount} disabled={isBusy} testId="count-location-complete-count">
                Done with count
              </QuietButton>
              {onFillUncounted ? (
                <QuietButton onClick={onFillUncounted} disabled={isBusy} testId="count-location-random-fill">
                  Fill uncounted with random counts (testing)
                </QuietButton>
              ) : null}
              <QuietButton onClick={onBack} disabled={isBusy} testId="count-location-back">
                Back to count
              </QuietButton>
              {onAbandon ? (
                <DangerButton onClick={onAbandon} disabled={isBusy} testId="count-location-abandon">
                  Abandon count
                </DangerButton>
              ) : null}
            </div>
          </>
        ) : isAbandonConfirm ? (
          <>
            <p className="ct-completion-abandon-copy">
              This erases the work you just did and does not save anything. Your previous saved progress is unaffected.
            </p>
            <div className="ct-completion-actions">
              <DangerButton
                onClick={() => runOnce(onConfirmAbandon)}
                disabled={isBusy}
                testId="count-abandon-confirm"
                solid={true}
              >
                Yes, abandon count
              </DangerButton>
              <QuietButton onClick={onBack} disabled={isBusy} testId="count-abandon-back">
                No, return to count
              </QuietButton>
            </div>
          </>
        ) : (
          <>
            <p className="ct-completion-lede">
              {isReview
                ? 'Review what will be saved before marking this count finished.'
                : 'Entered quantities are saved and this count is marked finished.'}
            </p>

            <div className="ct-completion-status-list">
              {isCompleted ? (
                <StatusRow icon={<SavedIcon />} testId="completion-saved-status">
                  Saved for inventory and reporting
                </StatusRow>
              ) : null}
              {eopCompleted ? (
                <StatusRow
                  icon={<CountedPieIcon counted={countedItems} total={totalItems} />}
                  testId="completion-eop-counted-status"
                >
                  {countedItems} of {totalItems} items counted
                </StatusRow>
              ) : null}
              {isReview ? (
                <>
                  <StatusRow
                    icon={<CountedPieIcon counted={countedItems} total={totalItems} />}
                    testId="completion-counted-status"
                  >
                    {countedItems} of {totalItems} items counted
                  </StatusRow>
                  {unaccountedItems > 0 ? (
                    <StatusRow
                      icon={<BlankCursorIcon />}
                      testId="completion-unaccounted-status"
                      detail={orderingEnabled
                        ? 'They are not counted as zero and are excluded from ordering.'
                        : 'They are not counted as zero.'}
                    >
                      {unaccountedItems === 1
                        ? '1 uncounted item remains blank'
                        : `${unaccountedItems} uncounted items remain blank`}
                    </StatusRow>
                  ) : null}
                </>
              ) : null}
              {renderOrderingRows()}
            </div>

            {isReview ? (
              <>
                <div className="ct-completion-meaning">
                  <h3>What completing means</h3>
                  <p>
                    Completing saves entered quantities and marks this count finished for inventory and reporting.
                    {orderingEnabled ? ' Ordering is a separate next step.' : ''}
                  </p>
                </div>
                <ErrorNotice>{error}</ErrorNotice>
                <div className="ct-completion-actions">
                  <PrimaryButton onClick={() => runOnce(onComplete)} disabled={isBusy} testId="count-review-complete">
                    {isBusy ? 'Completing count…' : 'Complete count'}
                  </PrimaryButton>
                  <QuietButton onClick={onBack} disabled={isBusy} testId="count-review-back">
                    Back to count
                  </QuietButton>
                  {onAbandon ? (
                    <DangerButton onClick={onAbandon} disabled={isBusy} testId="count-review-abandon">
                      Abandon count
                    </DangerButton>
                  ) : null}
                </div>
              </>
            ) : (
              <>
                <ErrorNotice>{error}</ErrorNotice>
                {renderEopValueSummary()}
                {renderEopReportLinks()}
                {renderCompletedNextStep()}
              </>
            )}
          </>
        )}
      </section>
    </div>
  );
}
